import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  TextField,
  Typography,
} from "@mui/material";
import { Lock } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, pageHeader } from "../theme/designSystem";
import {
  patientSensitiveDataService,
  ProfileSensitiveDataRow,
  SensitiveDataFlags,
  SensitiveGroup,
} from "../services/patientSensitiveDataService";

/**
 * Administrativo → Dados sensíveis do paciente (RN-112q, item 118): uma linha por perfil do
 * sistema, um interruptor por grupo. Ligado = o perfil VÊ o grupo. Tudo nasce desligado; cada
 * toque grava na hora (a matriz é pequena e o efeito é imediato para quem logar em seguida).
 */

const GROUPS: { key: SensitiveGroup; label: string; hint: string }[] = [
  { key: "documents", label: "Documentos", hint: "CPF, RG e documentos dos responsáveis" },
  { key: "emails", label: "E-mails", hint: "E-mails do paciente e dos responsáveis, inclusive o link de e-mail" },
  { key: "phones", label: "Telefones", hint: "Telefones e WhatsApp do paciente e dos responsáveis, inclusive o link do WhatsApp" },
  { key: "addresses", label: "Endereço", hint: "CEP, logradouro, número, complemento, bairro, cidade e UF" },
  { key: "healthPlan", label: "Convênio", hint: "Carteirinha, validade e titular dos convênios do paciente (aba Financeiro)" },
  { key: "editRegister", label: "Editar cadastro", hint: "Pode editar o cadastro de um paciente existente (criar paciente é outra permissão)" },
];

const PatientSensitiveData: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();

  const [rows, setRows] = useState<ProfileSensitiveDataRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"active" | "all">("active");

  const load = async () => {
    setLoading(true);
    try {
      setRows(await patientSensitiveDataService.getAll());
    } catch {
      showToast("Erro ao carregar os perfis.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Dados sensíveis do paciente";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return rows
      .filter((r) => statusFilter === "all" || r.active)
      .filter((r) => !term || normalize(r.profileName).includes(term));
  }, [rows, search, statusFilter]);

  const hasFilterChanges = search !== "" || statusFilter !== "active";
  const clearFilters = () => { setSearch(""); setStatusFilter("active"); };

  const toggle = (row: ProfileSensitiveDataRow, group: SensitiveGroup) =>
    saveFlags(row, { ...row.flags, [group]: !row.flags[group] });

  const releasedCount = (flags: SensitiveDataFlags) => GROUPS.filter((g) => flags[g.key]).length;

  const saveFlags = async (row: ProfileSensitiveDataRow, next: SensitiveDataFlags) => {
    const previous = rows;
    setRows((prev) => prev.map((r) => (r.profileId === row.profileId ? { ...r, flags: next, configured: true } : r)));
    setSavingProfile(row.profileId);
    try {
      await patientSensitiveDataService.save(row.profileId, next);
    } catch (e: any) {
      setRows(previous);
      showToast(e?.response?.data?.description || "Erro ao salvar a liberação.", "error");
    } finally {
      setSavingProfile(null);
    }
  };

  /** Marcar/desmarcar todos os grupos do perfil de uma vez (pedido do Hilton, 13/09). */
  const toggleAll = (row: ProfileSensitiveDataRow) => {
    const allOn = releasedCount(row.flags) === GROUPS.length;
    const next = GROUPS.reduce((acc, g) => ({ ...acc, [g.key]: !allOn }), { ...row.flags });
    return saveFlags(row, next);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Dados sensíveis do paciente</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Marcado = o perfil vê o grupo no cadastro e na lista de pacientes; "Editar cadastro" libera o botão Editar. Tudo começa desmarcado; a mudança vale no próximo acesso de quem usa o perfil.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Buscar perfil"
                  placeholder="Nome do perfil"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ maxLength: 60 }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Situação</InputLabel>
                  <Select label="Situação" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} notched displayEmpty>
                    <MenuItem value="active">Perfis ativos</MenuItem>
                    <MenuItem value="all">Todos</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
              </div>
            </div>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: colors.primary }} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
                <Lock sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
                <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>Nenhum perfil no filtro</Typography>
                <Typography variant="body2">Ajuste a busca ou a situação.</Typography>
              </Box>
            ) : (
              <div>
                <ResultCount shown={filtered.length} total={rows.length} />
                <Box className="c4u-table__header">
                  <Box className="admin-plans-header-cell" sx={{ flex: "2 1 200px", minWidth: 0 }}>Perfil</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px", display: "flex", justifyContent: "center" }} title="Marca ou desmarca todos os grupos do perfil">Todos</Box>
                  {GROUPS.map((g) => (
                    <Box key={g.key} className="admin-plans-header-cell" sx={{ flex: "1 1 0", minWidth: 110, display: "flex", justifyContent: "center", textAlign: "center", whiteSpace: "nowrap" }} title={g.hint}>
                      {g.label}
                    </Box>
                  ))}
                </Box>

                {filtered.map((row) => {
                  const saving = savingProfile === row.profileId;
                  const released = releasedCount(row.flags);
                  return (
                    <Box key={row.profileId} className="c4u-table__row" sx={{ opacity: row.active ? 1 : 0.65 }}>
                      <Box className="admin-plans-cell" data-label="Perfil" sx={{ flex: "2 1 200px", minWidth: 0, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: colors.textPrimary }}>{row.profileName}</Typography>
                        {!row.active && <Chip label="Inativo" size="small" sx={{ backgroundColor: colors.textSecondary, color: "white", height: 20, fontSize: "0.7rem" }} />}
                        <Chip
                          label={released === 0 ? "Tudo bloqueado" : `${released} de ${GROUPS.length} liberados`}
                          size="small"
                          sx={{
                            height: 20, fontSize: "0.7rem", fontWeight: 600,
                            backgroundColor: released === 0 ? "#fff3cd" : "#e6f4ea",
                            color: released === 0 ? "#856404" : "#1e7e34",
                            border: `1px solid ${released === 0 ? "#ffe08a" : "#b7e1c1"}`,
                          }}
                        />
                      </Box>
                      <Box className="admin-plans-cell" data-label="Todos" sx={{ flex: "0 0 90px", display: "flex", justifyContent: { xs: "flex-end", md: "center" } }}>
                        <Checkbox
                          size="small"
                          checked={released === GROUPS.length}
                          indeterminate={released > 0 && released < GROUPS.length}
                          disabled={saving}
                          onChange={() => toggleAll(row)}
                          inputProps={{ "aria-label": `Todos os grupos para ${row.profileName}` }}
                          title="Marca ou desmarca todos os grupos do perfil"
                          sx={{ p: 0.5, color: "#adb5bd", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: colors.primary } }}
                        />
                      </Box>
                      {GROUPS.map((g) => (
                        <Box key={g.key} className="admin-plans-cell" data-label={g.label} sx={{ flex: "1 1 0", minWidth: 110, display: "flex", justifyContent: { xs: "flex-end", md: "center" } }}>
                          <Checkbox
                            size="small"
                            checked={row.flags[g.key]}
                            disabled={saving}
                            onChange={() => toggle(row, g.key)}
                            inputProps={{ "aria-label": `${g.label} para ${row.profileName}` }}
                            title={g.hint}
                            sx={{ p: 0.5, color: "#adb5bd", "&.Mui-checked": { color: colors.primary } }}
                          />
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </div>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default PatientSensitiveData;
